import React from 'react';
import { 
  Shield, Lock, Eye, Users, FileText, Server, Cookie, 
  CheckCircle2, Mail, Info, Globe, Smartphone, Database, Layers 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  const Section = ({ icon: Icon, title, children, color = "text-indigo-600", bgColor = "bg-indigo-50" }) => (
    <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow mb-8">
      <div className="flex items-center gap-4 mb-6 border-b border-slate-50 pb-4">
        <div className={`p-3 rounded-2xl ${bgColor} ${color}`}>
          <Icon size={26} />
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h3>
      </div>
      <div className="text-slate-600 leading-relaxed space-y-4 text-sm md:text-base text-justify">
        {children}
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-slate-50 pt-20 pb-20 px-6 font-sans text-slate-800 overflow-hidden">
      <SEO title="Privacy & Cookie Policy" description="Informativa completa sulla privacy e cookie di TusciaHub." url="/privacy" />
      
      <div className="absolute top-0 left-[-10%] w-[600px] h-[600px] bg-indigo-100/60 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>
      <div className="absolute bottom-0 right-[-10%] w-[500px] h-[500px] bg-purple-100/60 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>

      <div className="relative z-10 max-w-5xl mx-auto">
        <PageHeader title="Privacy & Cookie Policy" subtitle="Documento legale dettagliato sul trattamento dei dati." breadcrumbs={[{label: 'Privacy Policy'}]} />
        
        <div className="mb-10 p-6 bg-blue-50 border border-blue-100 rounded-3xl flex items-start gap-4 shadow-sm">
            <Info className="text-blue-600 shrink-0 mt-1" size={24} />
            <div>
                <p className="text-sm font-bold text-blue-900 mb-2 uppercase tracking-wide">Informativa ex artt. 13-14 Regolamento UE 2016/679 (GDPR)</p>
                <p className="text-sm text-blue-800 leading-relaxed">
                    Questo documento descrive come TusciaHub raccoglie, utilizza e protegge i tuoi dati personali. Utilizzando la piattaforma, acconsenti alle pratiche descritte in questa informativa.
                    <br/><br/>
                    <strong>Ultimo aggiornamento:</strong> {new Date().toLocaleDateString('it-IT')}
                </p>
            </div>
        </div>

        <Section icon={Shield} title="1. Titolare del Trattamento" color="text-indigo-600" bgColor="bg-indigo-50">
          <p>
            Il Titolare del Trattamento dei dati è il team di gestione di <strong>TusciaHub</strong> (progetto indipendente studentesco).
            <br/>
            Poiché il progetto non costituisce una persona giuridica formale ma un'iniziativa spontanea, per qualsiasi comunicazione relativa alla privacy, esercizio dei diritti o segnalazioni, è attivo il seguente indirizzo di contatto dedicato:
          </p>
          <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100 inline-flex items-center gap-3 font-bold text-indigo-700 mt-2">
            <Mail size={18}/> info@tusciahub.it
          </div>
        </Section>

        <Section icon={Database} title="2. Tipologia di Dati Raccolti" color="text-pink-600" bgColor="bg-pink-50">
          <p>Raccogliamo diverse categorie di dati per fornire e migliorare il servizio:</p>
          <ul className="space-y-4 mt-2 list-none">
            <li className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <strong className="block text-slate-900 mb-1">A. Dati forniti volontariamente dall'utente</strong>
                <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Dati di Registrazione:</strong> Indirizzo email istituzionale (@studenti.unitus.it) necessario per l'autenticazione.</li>
                    <li><strong>Dati di Profilo:</strong> Nome utente, immagine del profilo (avatar), informazioni accademiche (corso di laurea) se inserite.</li>
                    <li><strong>Contenuti Caricati:</strong> Dispense, appunti, immagini per annunci (Mercatino/Affitti), testi delle domande d'esame.</li>
                    <li><strong>Comunicazioni:</strong> Messaggi inviati al supporto tecnico.</li>
                </ul>
            </li>
            <li className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <strong className="block text-slate-900 mb-1">B. Dati raccolti automaticamente (Dati di Navigazione)</strong>
                <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Dati Tecnici:</strong> Indirizzo IP, tipo di browser, provider Internet (ISP), sistema operativo, data e ora della visita.</li>
                    <li><strong>Dati di Utilizzo:</strong> Pagine visitate, tempo di permanenza, click effettuati, errori di sistema.</li>
                    <li>Questi dati vengono utilizzati esclusivamente per fini statistici anonimi e per monitorare il corretto funzionamento del sito.</li>
                </ul>
            </li>
          </ul>
        </Section>

        <Section icon={Layers} title="3. Finalità e Base Giuridica" color="text-cyan-600" bgColor="bg-cyan-50">
          <p>Trattiamo i tuoi dati per le seguenti finalità:</p>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <strong className="text-slate-900">Erogazione del Servizio</strong>
                  <p className="text-xs mt-2">Consentire la registrazione, il login, il download/upload di materiali e la pubblicazione di annunci. <br/><span className="italic text-slate-400">Base giuridica: Esecuzione del contratto.</span></p>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <strong className="text-slate-900">Sicurezza</strong>
                  <p className="text-xs mt-2">Prevenire frodi, abusi, spam e accessi non autorizzati. <br/><span className="italic text-slate-400">Base giuridica: Legittimo interesse.</span></p>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <strong className="text-slate-900">Gamification (Classifica)</strong>
                  <p className="text-xs mt-2">Gestione del punteggio e visualizzazione della classifica pubblica. <br/><span className="italic text-slate-400">Base giuridica: Consenso/Esecuzione del servizio.</span></p>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <strong className="text-slate-900">Analisi Statistica</strong>
                  <p className="text-xs mt-2">Migliorare l'esperienza utente analizzando come viene usato il sito (dati aggregati). <br/><span className="italic text-slate-400">Base giuridica: Consenso (Cookie).</span></p>
              </div>
          </div>
        </Section>

        <Section icon={Eye} title="4. Visibilità Pubblica dei Dati (Classifica)" color="text-yellow-600" bgColor="bg-yellow-50">
          <p>
            TusciaHub integra un sistema di "Gamification" per incentivare la condivisione. 
            <strong>Attenzione:</strong> Partecipando attivamente alla piattaforma (caricando file), accetti che alcuni tuoi dati vengano resi pubblici nella <strong>Leaderboard (Classifica)</strong> presente nella Home Page.
          </p>
          <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-100 mt-4">
            <p className="font-bold text-yellow-900 mb-2">Dati visibili a TUTTI i visitatori:</p>
            <ul className="list-disc pl-5 space-y-1 text-yellow-800">
                <li><strong>Identificativo Parziale:</strong> La parte locale della tua email istituzionale (es. se la tua email è <em>mario.rossi@studenti...</em>, verrà visualizzato <strong>mario.rossi</strong>).</li>
                <li><strong>Punteggio Totale:</strong> Il saldo punti accumulato.</li>
                <li><strong>Posizione:</strong> Il tuo rank rispetto agli altri studenti.</li>
            </ul>
          </div>
          <p className="mt-4 text-xs">
            Se non desideri apparire in classifica, ti invitiamo a non caricare materiali o a contattare l'assistenza per la rimozione del tuo account.
          </p>
        </Section>

        <Section icon={Users} title="5. Destinatari dei Dati" color="text-green-600" bgColor="bg-green-50">
          <p>
            I tuoi dati non vengono MAI venduti a terzi per finalità di marketing. Possono essere condivisi con:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-2">
            <li><strong>Fornitori di Servizi Tecnici:</strong> Hosting provider (Vercel), fornitori di database e autenticazione (Supabase), servizi di analisi (Vercel Analytics). Tali soggetti agiscono come Responsabili del Trattamento e sono vincolati alla riservatezza.</li>
            <li><strong>Altri Utenti:</strong> Limitatamente ai dati necessari per le funzionalità social (es. nome utente su un annuncio del mercatino o autore di una dispensa).</li>
            <li><strong>Autorità Giudiziaria:</strong> Solo se richiesto obbligatoriamente per legge o per indagini su reati informatici.</li>
          </ul>
        </Section>

        <Section icon={Cookie} title="6. Cookie Policy Estesa" color="text-orange-600" bgColor="bg-orange-50">
          <p className="mb-4">
            Questo sito utilizza cookie e tecnologie simili per garantire il corretto funzionamento delle procedure e migliorare l'esperienza di uso delle applicazioni online.
          </p>
          
          <h4 className="font-bold text-lg mb-2 mt-6">Cosa sono i Cookie?</h4>
          <p className="mb-4 text-sm">
            I cookie sono brevi frammenti di testo che permettono al server web di memorizzare sul client (il browser) informazioni da riutilizzare nel corso della medesima visita al sito (cookie di sessione) o in seguito, anche a distanza di giorni (cookie persistenti).
          </p>

          <h4 className="font-bold text-lg mb-2 mt-6">Tipologie di Cookie utilizzati</h4>
          <div className="space-y-4">
              <div className="border-l-4 border-orange-400 pl-4 py-1">
                  <strong className="block text-slate-800">Cookie Tecnici (Necessari)</strong>
                  <p className="text-xs text-slate-500">Indispensabili per il corretto funzionamento del sito (es. login, mantenimento della sessione). Non richiedono consenso. Senza questi cookie, il sito non funziona.</p>
              </div>
              <div className="border-l-4 border-blue-400 pl-4 py-1">
                  <strong className="block text-slate-800">Cookie Analitici (Vercel Analytics)</strong>
                  <p className="text-xs text-slate-500">Utilizzati per raccogliere informazioni, in forma aggregata e anonima, sul numero degli utenti e su come questi visitano il sito. Poiché l'IP viene anonimizzato, sono equiparati ai cookie tecnici.</p>
              </div>
              <div className="border-l-4 border-purple-400 pl-4 py-1">
                  <strong className="block text-slate-800">Cookie di Terze Parti</strong>
                  <p className="text-xs text-slate-500">Visitando il sito potresti ricevere cookie da siti gestiti da altre organizzazioni (es. Supabase per l'autenticazione). La gestione delle informazioni raccolte da "terze parti" è disciplinata dalle relative informative.</p>
              </div>
          </div>

          <h4 className="font-bold text-lg mb-2 mt-6">Gestione dei Cookie</h4>
          <p className="text-sm">
            L'utente può decidere se accettare o meno i cookie utilizzando le impostazioni del proprio browser. La disabilitazione totale o parziale dei cookie tecnici può compromettere l'utilizzo delle funzionalità del sito riservate agli utenti registrati.
          </p>
        </Section>

        <Section icon={Globe} title="7. Trasferimento Dati Extra-UE" color="text-blue-600" bgColor="bg-blue-50">
          <p>
            Alcuni dei nostri fornitori tecnici (es. Supabase, Vercel) potrebbero avere server dislocati negli Stati Uniti o in altri paesi fuori dallo Spazio Economico Europeo. 
            Il trasferimento dei dati avviene sulla base delle Clausole Contrattuali Standard (SCC) approvate dalla Commissione Europea o altri meccanismi legali che garantiscono un livello di protezione adeguato dei dati personali.
          </p>
        </Section>

        <Section icon={Lock} title="8. Diritti dell'Interessato" color="text-red-600" bgColor="bg-red-50">
          <p>In qualità di interessato, ai sensi degli artt. 15-22 del GDPR, hai il diritto di:</p>
          <ul className="grid md:grid-cols-2 gap-3 mt-4 text-sm">
              <li className="flex gap-2"><CheckCircle2 className="text-green-500 shrink-0" size={16}/> Chiedere la conferma dell'esistenza dei tuoi dati.</li>
              <li className="flex gap-2"><CheckCircle2 className="text-green-500 shrink-0" size={16}/> Ottenere le indicazioni circa le finalità del trattamento.</li>
              <li className="flex gap-2"><CheckCircle2 className="text-green-500 shrink-0" size={16}/> Richiedere l'aggiornamento, la rettifica o l'integrazione dei dati.</li>
              <li className="flex gap-2"><CheckCircle2 className="text-green-500 shrink-0" size={16}/> Chiedere la cancellazione (Diritto all'oblio).</li>
              <li className="flex gap-2"><CheckCircle2 className="text-green-500 shrink-0" size={16}/> Chiedere la limitazione del trattamento.</li>
              <li className="flex gap-2"><CheckCircle2 className="text-green-500 shrink-0" size={16}/> Opporti al trattamento per motivi legittimi.</li>
          </ul>
          <p className="mt-4 text-sm bg-red-50 p-3 rounded border border-red-100">
            Per esercitare i tuoi diritti, invia una richiesta a: <strong>info@tusciahub.it</strong>. Risponderemo entro 30 giorni.
          </p>
        </Section>

      </div>
    </div>
  );
};

export default PrivacyPolicy;